import { useState, useMemo } from "react";
import {
  UserGroupIcon,
  TrophyIcon,
  CalendarIcon,
  MapPinIcon,
  PlusIcon,
  PencilSquareIcon,
  // PencilIcon,
  // EyeIcon,
} from "@heroicons/react/24/outline";
import StoryCard from "../components/StoryCard";
import CreateEventModal from "../components/CreateEventModal";
import CreateStoryModal from "../components/CreateStoryModal";
import EditEventModal from "../components/EditEventModal";
import AttendeesModal from "../components/AttendeesModal";
import { cn } from "../utils/cn";

// Supabase
import useCollection from "../hooks/useCollection";
import { useAuth } from "../context/AuthContext";
import { supabase, TABLES } from "../supabase";
import { useCommunityStories } from "../hooks/useCommunityStories";

export default function Community() {
  const { user } = useAuth();

  // Hooks to fetch Supabase collections with refresh functionality
  const { data: communityStories = [], isLoading: storiesLoading } =
    useCommunityStories();

  const { documents: users = [], loading: usersLoading } = useCollection(
    TABLES.USERS
  );

  const {
    documents: events = [],
    loading: eventsLoading,
    refresh: refreshEvents,
  } = useCollection(TABLES.EVENTS);

  const [activeTab, setActiveTab] = useState("stories");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [createEventModalOpen, setCreateEventModalOpen] = useState(false);
  const [createStoryModalOpen, setCreateStoryModalOpen] = useState(false);
  const [editingStory, setEditingStory] = useState<any>(null);
  const [joinLoading, setJoinLoading] = useState<string | null>(null);
  const [editEventModalOpen, setEditEventModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<any>(null);
  const [attendeesModalOpen, setAttendeesModalOpen] = useState(false);
  const [selectedEventForAttendees, setSelectedEventForAttendees] =
    useState<any>(null);

  const tabs = [
    { id: "stories", name: "Community Stories", icon: UserGroupIcon },
    { id: "volunteers", name: "Volunteers", icon: UserGroupIcon },
    { id: "leaderboard", name: "Leaderboard", icon: TrophyIcon },
    { id: "events", name: "Events", icon: CalendarIcon },
  ];

  const storyCategories = ["all", "success", "impact", "community"];

  const filteredStories = useMemo(() => {
    if (selectedCategory === "all") return communityStories;
    return (communityStories as any[]).filter(
      (story: any) => story.category === selectedCategory
    );
  }, [selectedCategory, communityStories]);

  // Volunteers: users with role 'volunteer'
  const volunteers = useMemo(
    () => users.filter((user: any) => user.role === "volunteer"),
    [users]
  );

  // Leaderboard: top 10 by impactScore
  const leaderboard = useMemo(() => {
    return [...users]
      .sort(
        (a: any, b: any) =>
          (b.stats?.impactScore || 0) - (a.stats?.impactScore || 0)
      )
      .slice(0, 10);
  }, [users]);

  // Filter upcoming events (future dates only)
  const upcomingEvents = useMemo(() => {
    const now = new Date();
    return events
      .filter((event: any) => {
        const eventDate = event.event_date?.toDate
          ? event.event_date.toDate()
          : new Date(event.event_date || event.date);
        return eventDate > now;
      })
      .sort((a: any, b: any) => {
        const dateA = a.event_date?.toDate
          ? a.event_date.toDate()
          : new Date(a.event_date || a.date);
        const dateB = b.event_date?.toDate
          ? b.event_date.toDate()
          : new Date(b.event_date || b.date);
        return dateA.getTime() - dateB.getTime();
      });
  }, [events]);

  // Utility to handle Firestore Timestamp toDate() or fallbacks
  function safeDate(val: any) {
    if (!val) return "";
    if (typeof val === "string") return new Date(val);
    if (val.toDate) return val.toDate();
    return new Date(val);
  }

  const handleEditEvent = (event: any) => {
    setEditingEvent(event);
    setEditEventModalOpen(true);
  };

  const handleCloseEditEventModal = () => {
    setEditEventModalOpen(false);
    setEditingEvent(null);
  };

  const handleViewAttendees = (event: any) => {
    setSelectedEventForAttendees(event);
    setAttendeesModalOpen(true);
  };

  const handleCloseAttendeesModal = () => {
    setAttendeesModalOpen(false);
    setSelectedEventForAttendees(null);
  };

  const handleEditStory = (story: any) => {
    setEditingStory(story);
    setCreateStoryModalOpen(true);
  };

  const handleCloseStoryModal = () => {
    setCreateStoryModalOpen(false);
    setEditingStory(null);
  };

  const handleJoinEvent = async (eventId: string) => {
    if (!user) {
      alert("Please sign in to join events.");
      return;
    }

    setJoinLoading(eventId);
    try {
      const event = events.find((e: any) => e.id === eventId);
      const currentAttendees = event?.attendee_ids || event?.attendees || [];
      const isAlreadyJoined = currentAttendees.includes(user.id);

      if (isAlreadyJoined) {
        // Leave event
        const updatedAttendees = currentAttendees.filter(
          (id: string) => id !== user.id
        );
        const { error: updateError } = await supabase
          .from(TABLES.EVENTS)
          .update({
            attendee_ids: updatedAttendees,
            current_attendees: Math.max(
              0,
              (event.current_attendees || event.attendee_count || 0) - 1
            ),
            updated_at: new Date().toISOString(),
          })
          .eq("id", eventId);

        if (updateError) throw updateError;

        // Create notification for event creator
        if (
          (event.organizer_id || event.created_by_id) &&
          (event.organizer_id || event.created_by_id) !== user.id
        ) {
          const { error: notificationError } = await supabase
            .from(TABLES.NOTIFICATIONS)
            .insert({
              user_id: event.organizer_id || event.created_by_id,
              type: "event_left",
              title: "Someone Left Your Event",
              message: `${
                user.user_metadata?.name || user.email
              } left your event: ${event.title}`,
              is_read: false,
              created_at: new Date().toISOString(),
              related_id: eventId,
            });

          if (notificationError) {
            console.error("Failed to create notification:", notificationError);
          }
        }
      } else {
        // Join event
        const updatedAttendees = [...currentAttendees, user.id];
        const { error: updateError } = await supabase
          .from(TABLES.EVENTS)
          .update({
            attendee_ids: updatedAttendees,
            current_attendees:
              (event.current_attendees || event.attendee_count || 0) + 1,
            updated_at: new Date().toISOString(),
          })
          .eq("id", eventId);

        if (updateError) throw updateError;

        // Create notification for event creator
        if (
          (event.organizer_id || event.created_by_id) &&
          (event.organizer_id || event.created_by_id) !== user.id
        ) {
          const { error: notificationError } = await supabase
            .from(TABLES.NOTIFICATIONS)
            .insert({
              user_id: event.organizer_id || event.created_by_id,
              type: "event_joined",
              title: "New Event Attendee!",
              message: `${
                user.user_metadata?.name || user.email
              } joined your event: ${event.title}`,
              is_read: false,
              created_at: new Date().toISOString(),
              related_id: eventId,
            });

          if (notificationError) {
            console.error("Failed to create notification:", notificationError);
          }
        }

        // Create notification for the user who joined
        const { error: joinNotificationError } = await supabase
          .from(TABLES.NOTIFICATIONS)
          .insert({
            user_id: user.id,
            type: "event_joined_confirmation",
            title: "Event Joined Successfully!",
            message: `You have successfully joined: ${event.title}`,
            is_read: false,
            created_at: new Date().toISOString(),
            related_id: eventId,
          });

        if (joinNotificationError) {
          console.error(
            "Failed to create join notification:",
            joinNotificationError
          );
        }
      }

      // Refresh events after join/leave
      refreshEvents();
    } catch (error) {
      console.error("Error joining/leaving event:", error);
      alert("Failed to update event attendance. Please try again.");
    }
    setJoinLoading(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50/20">
      {/* Header */}
      <div className="bg-white/90 backdrop-blur-2xl shadow-2xl border-b border-white/30">
        <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="mb-3 text-2xl font-bold text-slate-900 lg:text-3xl">
              MealBridge Community
            </h1>
            <p className="text-base text-slate-600 font-medium">
              Connect with fellow food heroes and share your impact stories
            </p>
          </div>
        </div>
      </div>

      <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Tabs */}
        <div className="mb-8">
          <nav className="flex flex-wrap gap-4 p-2 bg-white/80 backdrop-blur-xl rounded-2xl shadow-xl border border-white/30">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "flex items-center space-x-2 py-2 px-4 rounded-lg font-semibold text-sm transition-all duration-300 transform hover:scale-105",
                  activeTab === tab.id
                    ? "bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 text-white shadow-xl"
                    : "text-slate-600 hover:bg-slate-100 hover:shadow-lg"
                )}
              >
                <tab.icon className="w-6 h-6" />
                <span>{tab.name}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Stories Tab */}
        {activeTab === "stories" && (
          <div>
            <div className="flex flex-col gap-4 mb-6 lg:flex-row lg:items-center lg:justify-between">
              <h2 className="text-xl font-bold text-slate-900 lg:text-2xl">
                Community Stories
              </h2>
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
                {user && (
                  <button
                    className="flex items-center justify-center space-x-2 px-6 py-3 text-sm font-bold text-white transition-all duration-500 transform bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 rounded-xl shadow-xl hover:shadow-2xl hover:-translate-y-1 hover:scale-105"
                    onClick={() => {
                      setEditingStory(null);
                      setCreateStoryModalOpen(true);
                    }}
                  >
                    <PencilSquareIcon className="w-6 h-6" />
                    <span>Share Your Story</span>
                  </button>
                )}
                <div className="flex flex-wrap gap-3">
                  {storyCategories.map((category) => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={cn(
                        "px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 transform hover:scale-105 capitalize",
                        selectedCategory === category
                          ? "bg-gradient-to-r from-purple-100 to-pink-100 text-purple-800 shadow-lg"
                          : "bg-white/80 backdrop-blur-sm text-slate-600 hover:bg-slate-100 hover:shadow-md border border-slate-200"
                      )}
                    >
                      {category === "all" ? "All Stories" : category}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {storiesLoading ? (
              <div className="py-12 text-center">
                <div className="inline-flex items-center px-6 py-3 bg-slate-100 rounded-xl shadow-lg">
                  <div className="w-6 h-6 mr-3 border-3 rounded-full border-purple-500 border-t-transparent animate-spin"></div>
                  <span className="text-sm font-bold text-slate-600">
                    Loading stories...
                  </span>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                {filteredStories.map((story: any) => (
                  <StoryCard
                    key={story.id}
                    story={story}
                    onLike={(id) => console.log("Like story:", id)}
                    onEdit={handleEditStory}
                    currentUserId={user?.id}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Volunteers Tab */}
        {activeTab === "volunteers" && (
          <div>
            <div className="flex flex-col gap-4 mb-6 lg:flex-row lg:items-center lg:justify-between">
              <h2 className="text-xl font-bold text-slate-900 lg:text-2xl">
                Our Amazing Volunteers
              </h2>
              <button className="px-6 py-3 text-sm font-bold text-white transition-all duration-500 transform bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 rounded-xl shadow-xl hover:shadow-2xl hover:-translate-y-1 hover:scale-105">
                Become a Volunteer
              </button>
            </div>

            {usersLoading ? (
              <div className="py-20 text-center">
                <div className="inline-flex items-center px-8 py-4 bg-slate-100 rounded-2xl shadow-lg">
                  <div className="w-8 h-8 mr-4 border-3 rounded-full border-emerald-500 border-t-transparent animate-spin"></div>
                  <span className="text-lg font-bold text-slate-600">
                    Loading volunteers...
                  </span>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                {volunteers.map((volunteer: any) => (
                  <div key={volunteer.id} className="card-floating">
                    <div className="flex items-center mb-6 space-x-4">
                      {volunteer.profile_image_url || volunteer.photoURL ? (
                        <img
                          src={
                            volunteer.profile_image_url || volunteer.photoURL
                          }
                          alt={volunteer.name}
                          className="object-cover w-20 h-20 rounded-2xl shadow-lg"
                        />
                      ) : (
                        <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-gradient-to-br from-emerald-100 to-teal-100 shadow-lg">
                          <span className="text-lg font-bold text-emerald-600">
                            {volunteer.name?.charAt(0)?.toUpperCase() || "U"}
                          </span>
                        </div>
                      )}
                      <div>
                        <h3 className="text-lg font-bold text-slate-900">
                          {volunteer.name}
                        </h3>
                        <p className="text-sm text-slate-600 font-medium">
                          {volunteer.location}
                        </p>
                        <div className="flex items-center mt-2 text-sm text-slate-500">
                          <CalendarIcon className="w-4 h-4 mr-2" />
                          Joined{" "}
                          {volunteer.joinedAt
                            ? safeDate(volunteer.joinedAt).toLocaleDateString()
                            : "—"}
                        </div>
                      </div>
                    </div>

                    <div className="mb-6 space-y-3">
                      <div className="flex justify-between text-base">
                        <span className="text-slate-600 font-bold">
                          Volunteer Hours:
                        </span>
                        <span className="font-bold text-emerald-600">
                          {volunteer.stats?.volunteersHours || 0}h
                        </span>
                      </div>
                      <div className="flex justify-between text-base">
                        <span className="text-slate-600 font-bold">
                          Impact Score:
                        </span>
                        <span className="font-bold text-emerald-600">
                          {volunteer.stats?.impactScore || 0}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {(volunteer.stats?.badges || []).map((badge: any) => (
                        <span
                          key={badge.id}
                          className="inline-flex items-center px-3 py-2 text-sm font-bold text-amber-800 bg-gradient-to-r from-amber-100 to-yellow-100 rounded-2xl shadow-lg"
                          title={badge.description}
                        >
                          {badge.icon} {badge.name}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Leaderboard Tab */}
        {activeTab === "leaderboard" && (
          <div>
            <div className="flex flex-col gap-4 mb-6 lg:flex-row lg:items-center lg:justify-between">
              <h2 className="text-xl font-bold text-slate-900 lg:text-2xl">
                Community Leaderboard
              </h2>
              <select
                className="w-auto input-field"
                aria-label="Select time period"
              >
                <option>This Month</option>
                <option>All Time</option>
                <option>This Year</option>
              </select>
            </div>

            <div className="overflow-hidden bg-white/95 backdrop-blur-2xl rounded-3xl shadow-2xl border border-white/30">
              <div className="px-8 py-6 border-b-2 border-slate-200">
                <h3 className="text-lg font-bold text-slate-900">
                  Top Contributors
                </h3>
              </div>
              <div className="divide-y divide-slate-200">
                {leaderboard.map((user: any, index: number) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between px-8 py-6 hover:bg-slate-50/50 transition-colors duration-300"
                  >
                    <div className="flex items-center space-x-6">
                      <div
                        className={cn(
                          "flex items-center justify-center w-12 h-12 rounded-2xl font-bold text-lg shadow-lg",
                          index === 0
                            ? "bg-gradient-to-r from-yellow-100 to-amber-100 text-yellow-800"
                            : index === 1
                            ? "bg-gradient-to-r from-slate-100 to-gray-100 text-slate-800"
                            : index === 2
                            ? "bg-gradient-to-r from-orange-100 to-red-100 text-orange-800"
                            : "bg-gradient-to-r from-slate-50 to-gray-50 text-slate-600"
                        )}
                      >
                        {index + 1}
                      </div>
                      {user.profile_image_url || user.photoURL ? (
                        <img
                          src={user.profile_image_url || user.photoURL}
                          alt={user.name}
                          className="object-cover w-16 h-16 rounded-2xl shadow-lg"
                        />
                      ) : (
                        <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-100 to-teal-100 shadow-lg">
                          <span className="text-xl font-bold text-emerald-600">
                            {user.name?.charAt(0)?.toUpperCase() || "U"}
                          </span>
                        </div>
                      )}
                      <div>
                        <p className="text-lg font-bold text-slate-900">
                          {user.name}
                        </p>
                        <p className="text-sm text-slate-600 font-medium">
                          {user.location}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-emerald-600">
                        {user.stats?.impactScore || 0}
                      </p>
                      <p className="text-xs text-slate-500 font-bold">
                        Impact Score
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Events Tab */}
        {activeTab === "events" && (
          <div>
            <div className="flex flex-col gap-4 mb-6 lg:flex-row lg:items-center lg:justify-between">
              <h2 className="text-xl font-bold text-slate-900 lg:text-2xl">
                Upcoming Events
              </h2>
              <button
                className="flex items-center justify-center space-x-2 px-6 py-3 text-sm font-bold text-white transition-all duration-500 transform bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 rounded-xl shadow-xl hover:shadow-2xl hover:-translate-y-1 hover:scale-105"
                onClick={() => setCreateEventModalOpen(true)}
              >
                <PlusIcon className="w-6 h-6" />
                <span>Create Event</span>
              </button>
            </div>

            {eventsLoading ? (
              <div className="py-20 text-center">
                <div className="inline-flex items-center px-8 py-4 bg-slate-100 rounded-2xl shadow-lg">
                  <div className="w-8 h-8 mr-4 border-3 rounded-full border-cyan-500 border-t-transparent animate-spin"></div>
                  <span className="text-lg font-bold text-slate-600">
                    Loading events...
                  </span>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                {upcomingEvents.map((event: any) => {
                  const eventDate = event.event_date?.toDate
                    ? event.event_date.toDate()
                    : new Date(event.event_date || event.date);
                  const isJoined =
                    user &&
                    (event.attendee_ids?.includes(user.id) ||
                      event.attendees?.includes(user.id));
                  const isLoading = joinLoading === event.id;

                  return (
                    <div key={event.id} className="card-floating">
                      <div className="relative mb-6 overflow-hidden rounded-3xl">
                        <img
                          src={
                            event.image_url ||
                            event.image ||
                            "https://images.pexels.com/photos/6646918/pexels-photo-6646918.jpeg?auto=compress&cs=tinysrgb&w=800"
                          }
                          alt={event.title}
                          className="object-cover w-full h-56"
                        />
                        <div className="absolute px-4 py-2 bg-white/95 backdrop-blur-xl rounded-2xl top-4 right-4 shadow-xl">
                          <p className="text-sm font-bold text-slate-900">
                            {eventDate.toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                            })}
                          </p>
                        </div>
                      </div>

                      <div className="space-y-6">
                        <h3 className="text-lg font-bold text-slate-900">
                          {event.title}
                        </h3>
                        <p className="text-sm text-slate-600 font-medium">
                          {event.description}
                        </p>

                        <div className="space-y-4">
                          <div className="flex items-center text-base text-slate-600">
                            <CalendarIcon className="w-5 h-5 mr-3" />
                            <span className="font-bold">
                              {eventDate.toLocaleDateString("en-US", {
                                weekday: "long",
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              })}
                            </span>
                          </div>
                          <div className="flex items-center text-base text-slate-600">
                            <MapPinIcon className="w-5 h-5 mr-3" />
                            <span className="font-bold">
                              {typeof event.location === "object"
                                ? `${event.location?.address || ""}, ${
                                    event.location?.city || ""
                                  }`
                                : `${event.location || ""}, ${
                                    event.city || ""
                                  }`}
                            </span>
                          </div>
                          <div className="flex items-center text-base text-slate-600">
                            <UserGroupIcon className="w-5 h-5 mr-3" />
                            <span className="font-bold">
                              {event.current_attendees ||
                                event.attendee_count ||
                                0}{" "}
                              attending
                              {event.max_attendees &&
                                ` (max ${event.max_attendees})`}
                            </span>
                          </div>
                        </div>

                        {/* Check if current user is the event organizer */}
                        {user &&
                        (event.organizer_id === user.id ||
                          event.created_by_id === user.id) ? (
                          <div className="space-y-4">
                            <div className="flex space-x-3">
                              <button
                                className="flex items-center justify-center flex-1 space-x-3 px-6 py-3 text-lg font-bold text-slate-600 transition-all duration-300 transform bg-slate-100 rounded-2xl hover:bg-slate-200 hover:scale-105 shadow-lg"
                                onClick={() => handleEditEvent(event)}
                              >
                                <PencilSquareIcon className="w-5 h-5" />
                                <span>Edit Event</span>
                              </button>
                              <button
                                className="flex items-center justify-center flex-1 space-x-3 px-6 py-3 text-lg font-bold text-slate-600 transition-all duration-300 transform border-2 border-slate-200 rounded-2xl hover:bg-slate-50 hover:scale-105 shadow-lg"
                                onClick={() => handleViewAttendees(event)}
                              >
                                <UserGroupIcon className="w-5 h-5" />
                                <span>Attendees</span>
                              </button>
                            </div>
                            <p className="text-sm text-center text-slate-500 font-bold">
                              You organized this event
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <button
                              className={cn(
                                "w-full px-8 py-4 text-lg font-bold rounded-2xl transition-all duration-500 transform hover:scale-105 shadow-xl",
                                isJoined
                                  ? "bg-gradient-to-r from-red-500 to-pink-500 text-white hover:shadow-2xl"
                                  : "bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 text-white hover:shadow-2xl"
                              )}
                              onClick={() => handleJoinEvent(event.id)}
                              disabled={isLoading}
                            >
                              {isLoading
                                ? "Updating..."
                                : isJoined
                                ? "Leave Event"
                                : "Join Event"}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                {upcomingEvents.length === 0 && (
                  <div className="py-20 text-center col-span-full">
                    <div className="flex items-center justify-center w-32 h-32 mx-auto mb-8 bg-gradient-to-br from-slate-100 to-slate-200 rounded-3xl shadow-2xl">
                      <CalendarIcon className="w-16 h-16 text-slate-400" />
                    </div>
                    <h3 className="mb-4 text-2xl font-bold text-slate-900">
                      No upcoming events
                    </h3>
                    <p className="text-xl text-slate-600 font-medium">
                      Be the first to create an event for the community!
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Event Modal */}
      <CreateEventModal
        isOpen={createEventModalOpen}
        onClose={() => setCreateEventModalOpen(false)}
        onEventCreated={refreshEvents}
      />

      {/* Edit Event Modal */}
      <EditEventModal
        isOpen={editEventModalOpen}
        onClose={handleCloseEditEventModal}
        onEventUpdated={refreshEvents}
        event={editingEvent}
      />

      {/* Attendees Modal */}
      <AttendeesModal
        isOpen={attendeesModalOpen}
        onClose={handleCloseAttendeesModal}
        event={selectedEventForAttendees}
      />

      {/* Create Story Modal */}
      <CreateStoryModal
        isOpen={createStoryModalOpen}
        onClose={handleCloseStoryModal}
        onStoryCreated={() => window.location.reload()}
        editingStory={editingStory}
      />
    </div>
  );
}
